<template>
  <div>
    <h3>Logo</h3>

    <v-flex v-if="config" :style="{width: logo_width+'px'}">
      <v-card>
        <v-img :src="logo" :height="(logo_width / logo_ratio) + 'px'"></v-img>
        <v-card-title>Logo (120x32) (ratio: 3,75)</v-card-title>
        <v-card-actions>
          <v-btn @click.native="show_logo_upload = !show_logo_upload">
            upload
            <v-icon>{{ show_logo_upload ? 'keyboard_arrow_down' : 'keyboard_arrow_up' }}</v-icon>
          </v-btn>
        </v-card-actions>
        <v-slide-y-transition>
          <v-card-text v-show="show_logo_upload">
            <input
              type="file"
              :disabled="isSaving"
              @change="uploadLogo($event.target.files[0])"
              accept="image/*"
              class="input-file"
            />
          </v-card-text>
        </v-slide-y-transition>
      </v-card>
    </v-flex>
    <br />
    <h3>Hero Image</h3>

    <v-flex v-if="config" :style="{width: hero_width+'px'}">
      <v-card>
        <v-img :src="hero" :height="(hero_width / hero_ratio) + 'px'"></v-img>
        <v-card-title>Logo (1600 x 1067) (ratio: 1,5)</v-card-title>
        <v-card-actions>
          <v-btn @click.native="show_hero_upload = !show_hero_upload">
            upload
            <v-icon>{{ show_hero_upload ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
          </v-btn>
        </v-card-actions>
        <v-slide-y-transition>
          <v-card-text v-show="show_hero_upload">
            <v-progress-linear v-if="hero_upload_in_progress" v-bind:indeterminate="true"></v-progress-linear>
            <input
              v-else
              type="file"
              :disabled="isSaving"
              @change="uploadHero($event.target.files[0])"
              accept="image/*"
              class="input-file"
            />
          </v-card-text>
        </v-slide-y-transition>
      </v-card>
    </v-flex>

    <br />
    <h3>Color Configuration</h3>

    <isrs-settings-dialog
      :fields="fields"
      :input-form-fields-activated="configActivated"
      :input-form-data="config"
      @formDataChanged="config => saveConfig(config)"
    ></isrs-settings-dialog>
  </div>
</template>

<script>
import Amplify from 'aws-amplify'
import IsrsSettingsDialog from '../../components/IsrsSettingsDialog.vue'

import { mapActions, mapGetters } from 'vuex'

export default {
  components: { IsrsSettingsDialog },

  data() {
    return {
      configActivated: true,
      logo_width: 230,
      logo_ratio: 3.75,
      hero_width: 600,
      hero_ratio: 1.5,
      isSaving: false,
      isLoading: true,
      show_logo_upload: false,
      show_hero_upload: false,
      hero_upload_in_progress: false,
      error: '',
      fields: [
        {
          header: '1. Hero Color (Standard: White)',
          label: 'Please enter the hex/RGBA value of the color [Eg: #ffffff or rgba(255,255,255,1)]',
          key: 'heroTextColor',
          type: String
        },
        {
          header: '2. Field description Color (Standard: Grey)',
          label: 'Please enter the hex/RGBA value of the color [Eg: #ffffff or rgba(255,255,255,1)]',
          key: 'fieldTextColor',
          type: String
        },
        {
          header: '3. Highlight Color (Standard: Blackberry)',
          label: 'Please enter only the hex value of the color [Eg: #ffffff]',
          key: 'buttonColor',
          type: String
        },
        {
          header: '4. Text Color (Standard: White)',
          label:  'Please enter the hex/RGBA value of the color [Eg: #ffffff or rgba(255,255,255,1)]',
          key: 'buttonTextColor',
          type: String
        },
        {
          header: '5. Text Color 2 (Standard: Blue)',
          label:  'Please enter the hex/RGBA value of the color [Eg: #ffffff or rgba(255,255,255,1)]',
          key: 'pageTextColor',
          type: String
        },
        {
          header: '6. Box Color (Standard: Grey-Blue)',
          label:  'Please enter the hex/RGBA value of the color [Eg: #ffffff or rgba(255,255,255,1)]',
          key: 'parcelShopInfoBackgroundColor',
          type: String
        }

      ]
    }
  },

  mounted() {
    this.fetchConfig().then(() => {
      this.isLoading = false
    })
  },

  computed: {
    ...mapGetters('returnShopPortal', ['config']),

    logo() {
      return (
        this.config.logo ||
        `${profileConfig.endpoints.consumerAppUrl}/assets/logo.svg`
      )
    },

    hero() {
      return (
        this.config.hero ||
        `${profileConfig.endpoints.consumerAppUrl}/assets/hero.jpg`
      )
    }
  },

  methods: {
    ...mapActions('returnShopPortal', ['fetchConfig', 'saveConfig']),

    uuidv4() {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
      )
    },

    getFileExtension(fileType) {
      switch (fileType) {
        case 'image/jpg':
        case 'image/jpeg':
          return 'jpg'
        case 'image/png':
          return 'png'
        case 'image/gif':
          return 'png'
        default:
          throw new Error('unsupported media type: ' + fileType)
      }
    },

    uploadLogo(file) {
      const uuid = this.$route.params.returnShopPortalId
      const key = `${uuid}/logos/${this.uuidv4()}.${this.getFileExtension(
        file.type
      )}`
      const bucket = profileConfig.storage.bucket
      const s3Url = `https://s3.eu-central-1.amazonaws.com/${bucket}/public/${key}`

      this.upload(key, file).then(() => {
        this.config.logo = s3Url
        this.saveConfig(this.config)
        this.show_logo_upload = false
      })
    },

    uploadHero(file) {
      const uuid = this.$route.params.returnShopPortalId
      const key = `${uuid}/hero/${this.uuidv4()}.${this.getFileExtension(
        file.type
      )}`
      const bucket = profileConfig.storage.bucket
      const s3Url = `https://s3.eu-central-1.amazonaws.com/${bucket}/public/${key}`

      this.upload(key, file).then(() => {
        this.config.hero = s3Url
        this.saveConfig(this.config)
        this.show_hero_upload = false
      })
    },

    upload(key, file) {
      return Amplify.Storage.put(key, file, {
        //level: 'private',
        type: file.type
      }).catch(err => {
        this.error = err.message
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// Appearance.vue?00d84e88